import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';

import Form, { SubmitType } from '../../../../components/form';
import Address from '../../../../components/form/elements/address';
import Button from '../../../../components/form/elements/button';
import Group from '../../../../components/form/elements/group';
import Link from '../../../../components/form/elements/link';
import Text from '../../../../components/form/elements/text';
import TextArea from '../../../../components/form/elements/text-area';
import PartnerDepartment from '../../../../interfaces/partnerDepartment';
import { HUMAN_PARTNERS_VIEW } from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { route } from '../../../../services/utils';
import { useObservableState } from 'observable-hooks';
import Partner from '../../../../interfaces/partner';

const schema = yup.object().shape({
  name: yup.string().max(255).required(),
  address: yup.lazy((addressValue) => {
    if (typeof addressValue === 'object') {
      return yup.object().shape({
        value: yup.string().optional(),
      });
    } else {
      return yup.string().max(255).optional();
    }
  }),
});

export default function CreatePartnerDepartment() {
  const { t } = useTranslation();
  const history = useHistory();
  const { farmId, partnerId } = useParams<{
    farmId: string;
    partnerId: string;
  }>();
  const { partnerService, partnerDepartmentService } = useService();

  const [partner] = useObservableState<Partner>(
    () => partnerService.show(farmId, partnerId),
    null
  );

  useEffect(() => {
    if (partner && partner.not_editable === true) {
      history.push(route(HUMAN_PARTNERS_VIEW, { farmId, id: partner.id }));
    }
  }, [farmId, partner, history]);

  const submit = useCallback<SubmitType<PartnerDepartment>>(
    (form: any) => {
      form.address = form.address?.value;
      return [
        partnerDepartmentService.store(farmId, partnerId, form),
        () =>
          history.push(route(HUMAN_PARTNERS_VIEW, { farmId, id: partnerId })),
      ];
    },
    [partnerDepartmentService, history, partnerId, farmId]
  );

  const [body] = useState(() => [
    Group([
      Text('name'),
      Address('address', t('address')),
      TextArea('comment'),
    ]),
  ]);

  const [footer] = useState(() => [
    Button('create'),
    Link(route(HUMAN_PARTNERS_VIEW, { farmId, id: partnerId }), 'cancel'),
  ]);

  return (
    <Form
      title={t('pages.human.partners.create_department')}
      mode="create"
      body={body}
      footer={footer}
      submit={submit}
      schema={schema}
    />
  );
}
